/**
 * Schematic SVG diagram explaining the B-Spline MECO assistant parameters.
 *
 * r-z plane, z pointing up (SVG y inverted):
 *   P0 (LE) bottom-left → Bezier → P3 (TE) top-right
 *   Inlet straight: vertical segment below P0 (z_IN → z_LE)
 *   Outlet straight: horizontal segment right of P3 (length = L_OUT)
 *   α_LE: angle of Bezier tangent from axial (z) direction at P0
 *   α_TE: angle of Bezier tangent from radial (r) direction at P3
 *   L1/L2: tangent handle lengths at P0 / P3
 */
export default function MecoParamDiagram() {
  return (
    <svg
      viewBox="0 0 420 210"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-auto"
      aria-label="MECO parameter diagram"
    >
      <defs>
        <marker id="meco-arr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
          <path d="M 0,0 L 7,3.5 L 0,7 Z" fill="#374151" />
        </marker>
      </defs>

      {/* Background */}
      <rect width="420" height="210" fill="#f8fafc" rx="6" />

      {/* ── Faint reference guide lines ── */}
      {/* r_LE: vertical down to r-axis */}
      <line x1="90" y1="190" x2="90" y2="167" stroke="#d1d5db" strokeWidth="0.8" strokeDasharray="3,3" />
      {/* z_LE: horizontal left to z-axis */}
      <line x1="48" y1="167" x2="90" y2="167" stroke="#d1d5db" strokeWidth="0.8" strokeDasharray="3,3" />
      {/* z_IN: horizontal left to z-axis */}
      <line x1="48" y1="192" x2="90" y2="192" stroke="#d1d5db" strokeWidth="0.8" strokeDasharray="3,3" />
      {/* r_TE: vertical down to r-axis */}
      <line x1="340" y1="190" x2="340" y2="42" stroke="#d1d5db" strokeWidth="0.8" strokeDasharray="3,3" />
      {/* z_TE: horizontal left to z-axis */}
      <line x1="48" y1="42" x2="340" y2="42" stroke="#d1d5db" strokeWidth="0.8" strokeDasharray="3,3" />

      {/* ── Axes ── */}
      <line x1="48" y1="195" x2="405" y2="195" stroke="#374151" strokeWidth="1.5" markerEnd="url(#meco-arr)" />
      <line x1="48" y1="200" x2="48" y2="10"  stroke="#374151" strokeWidth="1.5" markerEnd="url(#meco-arr)" />
      <text x="410" y="199" fontSize="12" fill="#374151" fontFamily="ui-sans-serif,system-ui,sans-serif">r</text>
      <text x="40"  y="9"   fontSize="12" fill="#374151" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle">z</text>

      {/* ── Inlet straight (axial): from z_IN to z_LE at r = r_LE ── */}
      <line x1="90" y1="192" x2="90" y2="167" stroke="#374151" strokeWidth="2" />
      <circle cx="90" cy="192" r="3" fill="#374151" />

      {/* ── Bezier curve: M P0  C P1  P2  P3 ── */}
      {/* P0=(90,167) P1=(114,102) P2=(272,60) P3=(340,42) */}
      <path d="M 90,167 C 114,102 272,60 340,42" fill="none" stroke="#1e3a8a" strokeWidth="2.5" strokeLinejoin="round" />

      {/* ── Outlet straight (radial): from P3 rightward (L_OUT) ── */}
      <line x1="340" y1="42" x2="380" y2="42" stroke="#374151" strokeWidth="2" />

      {/* ── Tangent control handles (dashed) ── */}
      {/* P0 → P1 */}
      <line x1="90" y1="167" x2="114" y2="102" stroke="#9ca3af" strokeWidth="1.2" strokeDasharray="5,3" />
      {/* P2 → P3 */}
      <line x1="272" y1="60"  x2="340" y2="42"  stroke="#9ca3af" strokeWidth="1.2" strokeDasharray="5,3" />

      {/* ── Point markers ── */}
      {/* P0 (LE) on-curve circle */}
      <circle cx="90"  cy="167" r="5" fill="white" stroke="#1e3a8a" strokeWidth="2" />
      {/* P1 off-curve diamond */}
      <polygon points="114,96 120,102 114,108 108,102" fill="#1e3a8a" />
      {/* P2 off-curve diamond */}
      <polygon points="272,54 278,60 272,66 266,60" fill="#1e3a8a" />
      {/* P3 (TE) on-curve circle */}
      <circle cx="340" cy="42"  r="5" fill="white" stroke="#1e3a8a" strokeWidth="2" />

      {/* ── α_LE angle arc at P0 ──
           Axial direction = straight up = (0,-1) SVG.
           Tangent P0→P1 = (24,-65)/||(24,-65)|| ≈ (0.347,-0.938).
           Arc ref points at radius 17 from P0:
             axial ref   = (90, 167-17) = (90, 150)
             tangent ref = (90+17×0.347, 167-17×0.938) = (95.9, 151.1)
      */}
      {/* Short axial reference tick */}
      <line x1="90" y1="167" x2="90" y2="148" stroke="#dc2626" strokeWidth="1" strokeDasharray="2,2" />
      {/* Arc from axial to tangent, sweep CW */}
      <path d="M 90,150 A 17,17 0 0,1 95.9,151.1" fill="none" stroke="#dc2626" strokeWidth="1.5" />
      {/* α_LE label */}
      <text x="106" y="157" fontSize="10" fill="#dc2626" fontFamily="ui-sans-serif,system-ui,sans-serif" fontStyle="italic">α_LE</text>

      {/* ── α_TE angle arc at P3 ──
           Radial direction = (-1,0) going left from P3 (backward).
           Tangent P3→P2 (backward) = normalize(272-340, 60-42) = normalize(-68,18)/70.4 = (-0.966, 0.256)
           Arc ref points at radius 17 from P3:
             radial ref        = (340-17, 42)       = (323, 42)
             back-tangent ref  = (340-17×0.966, 42+17×0.256) = (323.6, 46.4)
      */}
      {/* Short radial reference tick */}
      <line x1="340" y1="42" x2="321" y2="42" stroke="#dc2626" strokeWidth="1" strokeDasharray="2,2" />
      {/* Arc from radial to back-tangent, sweep CW */}
      <path d="M 323,42 A 17,17 0 0,1 323.6,46.4" fill="none" stroke="#dc2626" strokeWidth="1.5" />
      {/* α_TE label */}
      <text x="305" y="58" fontSize="10" fill="#dc2626" fontFamily="ui-sans-serif,system-ui,sans-serif" fontStyle="italic">α_TE</text>

      {/* ── Dimension labels ── */}

      {/* Axis tick labels (blue) */}
      <text x="90"  y="207" fontSize="10" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle" fontStyle="italic">r_LE</text>
      <text x="340" y="207" fontSize="10" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle" fontStyle="italic">r_TE</text>
      <text x="43"  y="170" fontSize="10" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="end"    fontStyle="italic">z_LE</text>
      <text x="43"  y="45"  fontSize="10" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="end"    fontStyle="italic">z_TE</text>
      <text x="43"  y="196" fontSize="10" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="end"    fontStyle="italic">z_IN</text>

      {/* L1 label: midpoint of P0-P1 handle, offset left */}
      <text x="91"  y="131" fontSize="10" fill="#6b7280" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="end"    fontStyle="italic">L1</text>

      {/* L2 label: midpoint of P2-P3 handle, offset above */}
      <text x="308" y="46"  fontSize="10" fill="#6b7280" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle" fontStyle="italic">L2</text>

      {/* L_OUT label: above outlet straight */}
      <text x="360" y="36"  fontSize="10" fill="#1e40af" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle" fontStyle="italic">L_OUT</text>

      {/* Point labels */}
      <text x="76"  y="184" fontSize="9" fill="#1e3a8a" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle">P0 (LE)</text>
      <text x="340" y="32"  fontSize="9" fill="#1e3a8a" fontFamily="ui-sans-serif,system-ui,sans-serif" textAnchor="middle">P3 (TE)</text>
    </svg>
  );
}
